import { useRef, useState, useCallback, useEffect } from "react";
import api, { wsUrl } from "@/lib/appApi";

export function useChatRoulette() {
  const [status, setStatus] = useState("idle"); // idle | searching | connected
  const [messages, setMessages] = useState([]);
  const [partner, setPartner] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [error, setError] = useState(null);
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [maintenance, setMaintenance] = useState(false);

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const iceServersRef = useRef([{ urls: "stun:stun.l.google.com:19302" }]);
  const pendingCandidates = useRef([]);
  const monitorPcRef = useRef(null);
  const wantSearchRef = useRef(false);
  const stoppedRef = useRef(false);
  const heartbeatRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const send = useCallback((obj) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj));
    }
  }, []);

  const closePeer = useCallback(() => {
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (e) {}
      pcRef.current = null;
    }
    pendingCandidates.current = [];
    setRemoteStream(null);
  }, []);

  const setupPeer = useCallback(async (initiator) => {
    closePeer();
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    pcRef.current = pc;
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    }
    pc.ontrack = (e) => setRemoteStream(e.streams[0]);
    pc.onicecandidate = (e) => {
      if (e.candidate) send({ type: "signal", payload: { candidate: e.candidate } });
    };
    pc.onconnectionstatechange = () => {
      if (pcRef.current !== pc) return;
      const st = pc.connectionState;
      const requeue = () => {
        closePeer();
        setPartner(null); setRoomId(null); setMessages([]);
        setStatus("searching");
        send({ type: "next" });
      };
      if (st === "failed") {
        if (wantSearchRef.current) requeue();
      } else if (st === "disconnected") {
        setTimeout(() => {
          if (pcRef.current === pc && pc.connectionState === "disconnected" && wantSearchRef.current) requeue();
        }, 5000);
      }
    };
    if (initiator) {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      send({ type: "signal", payload: { sdp: pc.localDescription } });
    }
  }, [closePeer, send]);

  const onSignal = useCallback(async (payload) => {
    const pc = pcRef.current;
    if (!pc) return;
    if (payload.sdp) {
      await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
      for (const c of pendingCandidates.current) {
        try { await pc.addIceCandidate(c); } catch (e) {}
      }
      pendingCandidates.current = [];
      if (payload.sdp.type === "offer") {
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        send({ type: "signal", payload: { sdp: pc.localDescription } });
      }
    } else if (payload.candidate) {
      const cand = new RTCIceCandidate(payload.candidate);
      if (pc.remoteDescription && pc.remoteDescription.type) {
        try { await pc.addIceCandidate(cand); } catch (e) {}
      } else {
        pendingCandidates.current.push(cand);
      }
    }
  }, [send]);

  // Admin monitoring: this user streams camera to admin on request
  const onMonitorStart = useCallback(async (adminId) => {
    if (!localStreamRef.current) return;
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    monitorPcRef.current = pc;
    localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    pc.onicecandidate = (e) => {
      if (e.candidate) send({ type: "monitor_signal", admin_id: adminId, payload: { candidate: e.candidate } });
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    send({ type: "monitor_signal", admin_id: adminId, payload: { sdp: pc.localDescription } });
  }, [send]);

  const onMonitorSignal = useCallback(async (adminId, payload) => {
    const pc = monitorPcRef.current;
    if (!pc) return;
    if (payload.sdp) {
      await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
    } else if (payload.candidate) {
      try { await pc.addIceCandidate(new RTCIceCandidate(payload.candidate)); } catch (e) {}
    }
  }, []);

  const handleMessage = useCallback(async (msg) => {
    switch (msg.type) {
      case "waiting":
        setStatus("searching");
        break;
      case "matched":
        setPartner(msg.partner);
        setRoomId(msg.room_id);
        setMessages([]);
        setStatus("connected");
        await setupPeer(msg.initiator);
        break;
      case "signal":
        await onSignal(msg.payload);
        break;
      case "chat":
        setMessages((m) => [...m, { from: "partner", text: msg.text, ts: msg.ts }]);
        break;
      case "partner_left":
        closePeer();
        setPartner(null);
        setRoomId(null);
        if (wantSearchRef.current) {
          setStatus("searching");
          send({ type: "ready" });
        } else {
          setStatus("idle");
        }
        break;
      case "monitor_start":
        await onMonitorStart(msg.admin_id);
        break;
      case "monitor_signal":
        await onMonitorSignal(msg.admin_id, msg.payload);
        break;
      case "monitor_stop":
        if (monitorPcRef.current) { try { monitorPcRef.current.close(); } catch (e) {} monitorPcRef.current = null; }
        break;
      case "banned":
        wantSearchRef.current = false;
        stoppedRef.current = true;
        setError("Your account has been banned.");
        break;
      case "maintenance":
        wantSearchRef.current = false;
        if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
        closePeer();
        setPartner(null); setRoomId(null);
        setStatus("idle");
        setMaintenance(true);
        break;
      case "auth_error":
        setError("Authentication failed.");
        break;
      default:
        break;
    }
  }, [setupPeer, onSignal, closePeer, send, onMonitorStart, onMonitorSignal]);

  const ensureMedia = useCallback(async () => {
    if (localStreamRef.current) return true;
    try {
      const camId = localStorage.getItem("cam_device");
      const micId = localStorage.getItem("mic_device");
      const constraints = {
        video: camId ? { deviceId: { exact: camId } } : true,
        audio: micId ? { deviceId: { exact: micId } } : true,
      };
      let s;
      try {
        s = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (inner) {
        s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      }
      localStreamRef.current = s;
      setLocalStream(s);
      setCamOn(true);
      setMicOn(true);
      return true;
    } catch (e) {
      setError("Camera/microphone access is required. Please allow permissions.");
      return false;
    }
  }, []);

  const toggleCam = useCallback(() => {
    const s = localStreamRef.current;
    if (!s) return;
    const on = !camOn;
    s.getVideoTracks().forEach((t) => { t.enabled = on; });
    setCamOn(on);
  }, [camOn]);

  const toggleMic = useCallback(() => {
    const s = localStreamRef.current;
    if (!s) return;
    const on = !micOn;
    s.getAudioTracks().forEach((t) => { t.enabled = on; });
    setMicOn(on);
  }, [micOn]);

  const connectWs = useCallback(async () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return true;
    try {
      const cfg = await api.get("/config");
      iceServersRef.current = cfg.data.ice_servers;
      const res = await api.get("/auth/ws-token");
      const ws = new WebSocket(wsUrl(res.data.token));
      wsRef.current = ws;
      ws.onmessage = (e) => handleMessage(JSON.parse(e.data));
      ws.onclose = () => {
        if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
        // graceful auto-reconnect: only while the user still wants to be online
        if (wantSearchRef.current && !stoppedRef.current) {
          scheduleReconnect();
        } else {
          setStatus("idle");
        }
      };
      await new Promise((resolve, reject) => {
        ws.onopen = () => resolve();
        ws.onerror = (err) => reject(err);
        setTimeout(() => reject(new Error("timeout")), 8000);
      });
      // keepalive so proxies/ingress don't drop the idle socket
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
        }
      }, 25000);
      return true;
    } catch (e) {
      setError("Could not connect to server.");
      return false;
    }
  }, [handleMessage]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectRef.current || stoppedRef.current) return;
    const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 15000);
    setStatus("searching");
    reconnectRef.current = setTimeout(async () => {
      reconnectRef.current = null;
      reconnectAttempts.current += 1;
      if (!wantSearchRef.current || stoppedRef.current) return;
      const ok = await connectWs();
      if (ok) {
        reconnectAttempts.current = 0;
        closePeer();
        setPartner(null); setRoomId(null); setMessages([]);
        setStatus("searching");
        send({ type: "ready" });
      } else if (wantSearchRef.current && !stoppedRef.current) {
        scheduleReconnect();
      }
    }, delay);
  }, [connectWs, closePeer, send]);

  const start = useCallback(async () => {
    setError(null);
    stoppedRef.current = false;
    wantSearchRef.current = true;
    reconnectAttempts.current = 0;
    const ok = await ensureMedia();
    if (!ok) return;
    const wsok = await connectWs();
    if (!wsok) return;
    setStatus("searching");
    send({ type: "ready" });
  }, [ensureMedia, connectWs, send]);

  const next = useCallback(() => {
    closePeer();
    setPartner(null);
    setRoomId(null);
    setMessages([]);
    setStatus("searching");
    send({ type: "next" });
  }, [closePeer, send]);

  const stop = useCallback(() => {
    wantSearchRef.current = false;
    if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    closePeer();
    setPartner(null);
    setRoomId(null);
    setStatus("idle");
    send({ type: "stop" });
  }, [closePeer, send]);

  const sendChat = useCallback((text) => {
    const t = (text || "").trim();
    if (!t) return;
    send({ type: "chat", text: t });
    setMessages((m) => [...m, { from: "me", text: t, ts: new Date().toISOString() }]);
  }, [send]);

  useEffect(() => {
    return () => {
      stoppedRef.current = true;
      wantSearchRef.current = false;
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
      if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) {} }
      if (pcRef.current) { try { pcRef.current.close(); } catch (e) {} }
      if (localStreamRef.current) { localStreamRef.current.getTracks().forEach((t) => t.stop()); }
    };
  }, []);

  return {
    status, messages, partner, roomId, localStream, remoteStream, error,
    camOn, micOn, toggleCam, toggleMic, maintenance,
    start, next, stop, sendChat, setError,
  };
}
